import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { logoutApi, refreshTokenApi } from '@/api/auth';

export type AuthToken = {
  token: string;
  tokenType: string;
  expiresIn: number;
};

type AuthContextType = {
  isAuthenticated: boolean;
  isLoading: boolean;
  token: string | null;
  login: (authData: AuthToken) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    // ページ読み込み時にlocalStorageからトークンを取得
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('authToken');
      const expiresAt = localStorage.getItem('tokenExpiresAt');

      if (storedToken && expiresAt) {
        const now = Date.now();
        if (now < parseInt(expiresAt, 10)) {
          // トークンがまだ有効
          setToken(storedToken);
          setIsAuthenticated(true);
        } else {
          // トークンが期限切れの場合はリフレッシュを試行
          console.log('Token expired, attempting to refresh...');
          try {
            const authData = await refreshTokenApi();
            const newExpiresAt = Date.now() + authData.expiresIn;
            localStorage.setItem('authToken', authData.token);
            localStorage.setItem('tokenExpiresAt', newExpiresAt.toString());
            setToken(authData.token);
            setIsAuthenticated(true);
            console.log('Token refreshed successfully on initialization');
          } catch (error) {
            console.error('Token refresh failed on initialization:', error);
            // リフレッシュ失敗時はトークンをクリア
            localStorage.removeItem('authToken');
            localStorage.removeItem('tokenExpiresAt');
          }
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (authData: AuthToken): Promise<void> => {
    const expiresAt = Date.now() + authData.expiresIn;
    localStorage.setItem('authToken', authData.token);
    localStorage.setItem('tokenExpiresAt', expiresAt.toString());

    // flushSyncを使用してstate更新を同期的に実行
    flushSync(() => {
      setToken(authData.token);
      setIsAuthenticated(true);
    });
  };

  const refreshToken = async (): Promise<boolean> => {
    try {
      console.log('Refreshing token...');
      const authData = await refreshTokenApi();
      const expiresAt = Date.now() + authData.expiresIn;
      localStorage.setItem('authToken', authData.token);
      localStorage.setItem('tokenExpiresAt', expiresAt.toString());

      // flushSyncを使用してstate更新を同期的に実行
      flushSync(() => {
        setToken(authData.token);
        setIsAuthenticated(true);
      });

      console.log('Token refreshed successfully');
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      // リフレッシュ失敗時はログアウト
      await logout();
      return false;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      // ログアウトAPIを呼び出し
      await logoutApi();
    } catch (error) {
      console.error('Logout API error:', error);
      // API呼び出しが失敗してもログアウト処理は続行
    } finally {
      // トークンとCookieをクリア
      localStorage.removeItem('authToken');
      localStorage.removeItem('tokenExpiresAt');
      setToken(null);
      setIsAuthenticated(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, isLoading, token, login, logout, refreshToken }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
